import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  StyledModalBody,
  StyledModal,
  StyledModalFooter,
  QuestionContainer,
  QuestionText,
  QuestionInput,
  RadioGroup,
  RadioLabel,
  ScoreRadioGroup,
  QuestionsWrapper,
} from "../styledComponents/StyledModals";
import { StyledButton } from "../styledComponents/CommonStyledComponents";
import { Input } from "reactstrap";
import { MUTATION_SET_PRE_EVENT_ALERT } from "../graphql/mutations";
import { useApolloClient } from "@apollo/client";
import { toast } from "react-toastify";

const SCORE_OPTIONS = [
  { value: "0", labelKey: "label_never" },
  { value: "1", labelKey: "label_sometimes" },
  { value: "2", labelKey: "label_most_of_the_time" },
  { value: "3", labelKey: "label_almost_always" },
];

export const RiskScorePreEventDialog = ({
  showModal,
  riskScorePreEventData,
  skin,
  setIsOpenRiskScorePreEvent,
}) => {
  const { t } = useTranslation("");
  const questions = riskScorePreEventData.questions || [];
  const [answers, setAnswers] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const apolloClient = useApolloClient();

  const handleInputChange = (questionLabel, value) => {
    setAnswers((prev) => ({
      ...prev,
      [questionLabel]: value,
    }));

    // Clear validation error when user provides an answer
    if (validationErrors[questionLabel]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[questionLabel];
        return newErrors;
      });
    }
  };

  const validateAnswers = () => {
    const errors = {};
    questions.forEach((question) => {
      const answer = answers[question.label];
      const trimmedAnswer = answer?.trim() || "";
      const normalizedAnswer = trimmedAnswer.replace(/\s+/g, " ");

      if (!answer || trimmedAnswer === "") {
        errors[question.label] = t("answer_required");
      } else if (question.type === "INPUT" && normalizedAnswer.length < 14) {
        errors[question.label] = t("answer_minimum_14_characters");
      }
    });
    return errors;
  };

  const formatAnswersForMutation = () => {
    const formattedAnswers = questions.map((question) => ({
      question: question.label,
      answer: answers[question.label] || "",
    }));
    return JSON.stringify(formattedAnswers);
  };

  const handleProceedWithFormattedAnswers = async () => {
    if (isSubmitting) return; // Prevent multiple submissions

    const errorsForQuestions = validateAnswers();

    if (Object.keys(errorsForQuestions).length > 0) {
      setValidationErrors(errorsForQuestions);
      return;
    }

    setIsSubmitting(true);

    const eventAnswers = formatAnswersForMutation();
    const eventType = riskScorePreEventData.event_type;

    const { errors, data } = await apolloClient.mutate({
      mutation: MUTATION_SET_PRE_EVENT_ALERT,
      variables: { eventType: eventType, eventAnswers: eventAnswers },
      errorPolicy: "all",
    });
    if (errors) {
      toast.error(errors[0].message, {
        position: "bottom-center",
      });
      setIsSubmitting(false);
      return false;
    }
    if (data) {
      toast.success(t("label_success"), {
        position: "bottom-center",
      });
      setAnswers({});
      setValidationErrors({});
      setIsSubmitting(false);
      setIsOpenRiskScorePreEvent(false);
    }
  };

  const renderQuestion = (question, index) => {
    const { label, type } = question;
    const trimmedAnswer = answers[label]?.trim() || "";
    // Remove all extra spaces (keeping only single spaces between words)
    const normalizedLength = trimmedAnswer.replace(/\s+/g, " ").length;
    const isInputBelowMinimum =
      type === "INPUT" && normalizedLength > 0 && normalizedLength < 14;

    return (
      <QuestionContainer key={index}>
        <QuestionText>
          {t("label_" + label)}
          <span> *</span>
        </QuestionText>

        {type === "INPUT" && (
          <>
            <QuestionInput
              type="text"
              value={answers[label] || ""}
              onChange={(e) => handleInputChange(label, e.target.value)}
              placeholder="..."
            />
            {isInputBelowMinimum && (
              <span
                style={{
                  fontSize: "12px",
                  color: "#ff9800",
                  marginTop: "4px",
                  display: "block",
                }}
              >
                {t("label_characters")}
              </span>
            )}
          </>
        )}

        {type === "RADIO" && (
          <RadioGroup>
            <RadioLabel>
              <Input
                type="radio"
                name={label}
                value="yes"
                checked={answers[label] === "yes"}
                onChange={(e) => handleInputChange(label, e.target.value)}
              />
              {t("label_yes")}
            </RadioLabel>
            <RadioLabel>
              <Input
                type="radio"
                name={label}
                value="no"
                checked={answers[label] === "no"}
                onChange={(e) => handleInputChange(label, e.target.value)}
              />
              {t("label_no")}
            </RadioLabel>
          </RadioGroup>
        )}

        {type === "SCORE" && (
          <ScoreRadioGroup>
            {SCORE_OPTIONS.map((option) => (
              <RadioLabel key={option.value}>
                <Input
                  type="radio"
                  name={label}
                  value={option.value}
                  checked={answers[label] === option.value}
                  onChange={(e) => handleInputChange(label, e.target.value)}
                />
                {t(option.labelKey)}
              </RadioLabel>
            ))}
          </ScoreRadioGroup>
        )}
      </QuestionContainer>
    );
  };

  const allAnswersProvided = questions.every((question) => {
    const answer = answers[question.label];
    const trimmedAnswer = answer?.trim() || "";
    // Remove all extra spaces (keeping only single spaces between words)
    const normalizedAnswer = trimmedAnswer.replace(/\s+/g, " ");

    if (trimmedAnswer === "") return false;
    if (question.type === "INPUT" && normalizedAnswer.length < 14) return false;
    return true;
  });

  return (
    <StyledModal isOpen={showModal}>
      <StyledModalBody riskScore>
        <p
          dangerouslySetInnerHTML={{
            __html: t("label_" + riskScorePreEventData.event_type),
          }}
        />

        {questions.length > 0 && (
          <QuestionsWrapper>
            {questions
              .sort((a, b) => {
                // Extract number from labels like "rgd_q1", "rge_q2", etc.
                const getQuestionNumber = (label) => {
                  const match = label.match(/q(\d+)$/);
                  return match ? parseInt(match[1], 10) : 0;
                };

                return getQuestionNumber(a.label) - getQuestionNumber(b.label);
              })
              .map((question, index) => renderQuestion(question, index))}
            <p>
              {t("label_all_fields_required")} <span>*</span>
            </p>
          </QuestionsWrapper>
        )}
      </StyledModalBody>
      <StyledModalFooter position>
        <StyledButton
          btnBold
          banktransfer={skin === "cac"}
          pub={skin === "pub"}
          onClick={handleProceedWithFormattedAnswers}
          disabled={!allAnswersProvided || isSubmitting}
          style={{
            opacity: allAnswersProvided && !isSubmitting ? 1 : 0.6,
            cursor:
              allAnswersProvided && !isSubmitting ? "pointer" : "not-allowed",
          }}
        >
          {t("label_submit_answers")}
        </StyledButton>
      </StyledModalFooter>
    </StyledModal>
  );
};
